import { Info, X } from "lucide-react";
import { useState } from "react";
import { useTeam } from "@/modules/team/states";

interface ReopenedSessionBannerProps {
  sessionId: string;
  reopenedAt: string;
  reopenedBy: string;
}

export const ReopenedSessionBanner = ({
  sessionId,
  reopenedAt,
  reopenedBy,
}: ReopenedSessionBannerProps) => {
  // Temporary dismiss state (resets on page refresh)
  const [isDismissed, setIsDismissed] = useState(false);

  // Get team members to resolve name
  const { team } = useTeam();

  // Find member who reopened
  const reopenedByMember = team.find(
    (member) => member.member_id === reopenedBy
  );
  const memberName = reopenedByMember?.name || "Unknown Member";

  // Format date/time
  const formatReopenedDate = () => {
    const date = new Date(reopenedAt);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Handle dismiss (only for current session, resets on refresh)
  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Don't render if temporarily dismissed
  if (isDismissed) return null;

  return (
    <div 
      className="w-full flex flex-col justify-center items-start h-[44px] relative"
      style={{
        padding: '12px 16px',
        gap: '8px',
        isolation: 'isolate',
        background: 'linear-gradient(0deg, rgba(245, 165, 36, 0.1), rgba(245, 165, 36, 0.1)), rgba(255, 255, 255, 0.7)',
        borderBottom: '1px solid #F5A524',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <div 
        className="flex flex-row items-center"
        style={{
          padding: '0px',
          gap: '8px',
        }}
      >
        <Info 
          className="flex-none"
          style={{ 
            width: '20px', 
            height: '20px',
            color: '#C4841D',
          }} 
        />
        <p 
          className="text-sm font-medium flex-none"
          style={{ 
            color: '#C4841D',
            fontFamily: 'Inter',
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Session reopened on {formatReopenedDate()} by {memberName}.
        </p>
      </div>
      <button
        onClick={handleDismiss}
        className="flex flex-row items-center justify-center absolute transition-opacity hover:opacity-80"
        style={{
          width: '20px',
          height: '20px',
          right: '16px',
          top: 'calc(50% - 10px)',
          padding: '2px',
          gap: '4px',
          background: 'linear-gradient(0deg, rgba(245, 165, 36, 0.2), rgba(245, 165, 36, 0.2)), rgba(255, 255, 255, 0.7)',
          border: '1px solid #F5A524',
          backdropFilter: 'blur(1px)',
          WebkitBackdropFilter: 'blur(1px)',
          borderRadius: '24px',
        }}
        aria-label="Dismiss banner"
      >
        <X 
          style={{ 
            width: '16px', 
            height: '16px',
            color: '#F5A524',
          }} 
        />
      </button>
    </div>
  );
};
